import {
  type AreaModel,
  type BuyerModel,
  type CategoryModel,
  type DietModel,
  type FarmModel,
  type FarmKeeperModel,
  type ItemModel,
  type LotModel,
  type SupplierModel,
} from '@@types/models';
import { type GainCurveModel } from '@@types/models/gainCurve-model';
import { type ProgramModel } from '@@types/models/program-model';
import { api } from '@services/api';
import { queryKeys } from '@services/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from '@services/toast';
import { AuthContext } from './AuthContext';

type FarmProviderProps = {
  children: ReactNode;
};

type FarmInfo = {
  scores?: any[];
  scoreMeasurement?: 'percentage';
  keepers?: FarmKeeperModel[];
};

type LicenseMode = 'CONFINEMENT_ONLY' | 'PASTURE_ONLY' | 'BOTH';
type LicenseFeature = 'automation';

type LicenseEntitlement = {
  mode: LicenseMode;
  features: LicenseFeature[];
};

type FarmContextData = {
  farm: string | undefined;
  farms: FarmModel[] | undefined;
  categories?: CategoryModel[] | undefined;
  diets?: DietModel[] | undefined;
  areas?: AreaModel[] | undefined;
  items?: ItemModel[] | undefined;
  treatments?: string[] | undefined;
  scores?: any[] | undefined;
  farmInfo?: FarmInfo | undefined;
  programs?: ProgramModel[] | undefined;
  lots?: LotModel[] | undefined;
  gainCurves?: GainCurveModel[] | undefined;
  suppliers?: SupplierModel[] | undefined;
  buyers?: BuyerModel[] | undefined;
  licenseMode?: LicenseMode;
  licenseFeatures?: LicenseFeature[];
  update?: boolean;
  selectedDiet?: DietModel | undefined;
  setSelectedDiet?: (diet: DietModel | undefined) => void;
  selectFarm: (id: string) => void;
};

export const FarmContext = createContext({} as FarmContextData);

const FARM_STORAGE_KEY = '@groud:selected-farm';

export function FarmProvider({ children }: FarmProviderProps) {
  const { user } = useContext(AuthContext);
  const [update, setUpdate] = useState<boolean>(false);
  const [farm, setFarm] = useState<string | undefined>(() => {
    const storedFarm = localStorage.getItem(FARM_STORAGE_KEY);
    return storedFarm || undefined;
  });
  const [selectedDiet, setSelectedDiet] = useState<DietModel | undefined>();

  async function fetchModel<T>(model: string, farmId: string | undefined): Promise<T | null> {
    if (!model || !farmId) {
      return null;
    }

    const endpoints: Record<string, string> = {
      diet: `/farms/${farmId}/diets/all`,
      area: `/farms/${farmId}/areas/all`,
      lot: `/farms/${farmId}/lots/all`,
      gainCurve: `/farms/${farmId}/gain-curves/all`,
      program: `/farms/${farmId}/programs/all`,
      item: `/farms/${farmId}/items/all`,
      supplier: `/farms/${farmId}/suppliers/all`,
      buyer: `/farms/${farmId}/buyers/all`,
    };
    const endpoint = endpoints[model];
    if (!endpoint) {
      throw new Error(`Unsupported farm model "${model}"`);
    }
    const result = await api.get(endpoint);
    return result.data;
  }

  const selectFarm = (id: string) => {
    if (!id || id === farm) {
      return;
    }

    setFarm(id);
    localStorage.setItem(FARM_STORAGE_KEY, id);
  };

  const onError = (error: any) => {
    // 403 aqui significa usuário sem permissão/licença para um dos modelos
    // auxiliares (dietas, áreas, programas...). É condição esperada para
    // usuários restritos — o backend continua sendo a fonte de verdade — e
    // não deve virar toast de erro em toda carga do app.
    if (error?.response?.status === 403) {
      return;
    }
    toast.errorFrom(error, 'Não foi possível.', undefined, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      type: 'error',
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light',
    });
  };

  const farmsQuery = useQuery<FarmModel[]>({
    queryKey: queryKeys.farms.all(user?.id),
    queryFn: async () => {
      const result = await api.get('/farms');
      return result.data?.docs;
    },
  });

  // O endpoint devolve `mode` (legado) e `features` (aditivo). Uma query só
  // porque é uma leitura só — separar criaria duas chamadas para a mesma linha.
  const licenseModeQuery = useQuery<LicenseEntitlement>({
    queryKey: queryKeys.licenseMode(farm),
    queryFn: async () => {
      const result = await api.get(`/farms/${farm}/license-mode`);
      return {
        mode: (result.data?.mode as LicenseMode) || 'BOTH',
        features: Array.isArray(result.data?.features)
          ? (result.data.features as LicenseFeature[])
          : [],
      };
    },
    enabled: !!farm,
  });

  const farmInfoQuery = useQuery<FarmInfo | null>({
    queryKey: queryKeys.farms.single(farm),
    queryFn: async () => {
      const result = await api.get(`/farms/${farm}`);
      return result.data;
    },
    enabled: !!farm,
  });

  const dietsQuery = useQuery<DietModel[] | null>({
    queryKey: queryKeys.diets.all(farm),
    queryFn: async () => await fetchModel<DietModel[]>('diet', farm),
    enabled: !!farm,
  });

  const areasQuery = useQuery<AreaModel[] | null>({
    queryKey: queryKeys.areas.all(farm),
    queryFn: async () => await fetchModel<AreaModel[]>('area', farm),
    enabled: !!farm,
  });

  const lotsQuery = useQuery<LotModel[] | null>({
    queryKey: queryKeys.lots.all(farm),
    queryFn: async () => await fetchModel<LotModel[]>('lot', farm),
    enabled: !!farm,
  });

  const gainCurvesQuery = useQuery<GainCurveModel[] | null>({
    queryKey: ['gainCurves', farm],
    queryFn: async () => await fetchModel<GainCurveModel[]>('gainCurve', farm),
    enabled: !!farm,
  });

  const programsQuery = useQuery<ProgramModel[] | null>({
    queryKey: queryKeys.programs.all(farm),
    queryFn: async () => await fetchModel<ProgramModel[]>('program', farm),
    enabled: !!farm,
  });

  const itemsQuery = useQuery<ItemModel[] | null>({
    queryKey: queryKeys.items.all(farm),
    queryFn: async () => await fetchModel<ItemModel[]>('item', farm),
    enabled: !!farm,
  });

  const suppliersQuery = useQuery<SupplierModel[] | null>({
    queryKey: queryKeys.suppliers.all(farm),
    queryFn: async () => await fetchModel<SupplierModel[]>('supplier', farm),
    enabled: !!farm,
  });

  const buyersQuery = useQuery<BuyerModel[] | null>({
    queryKey: queryKeys.buyers.all(farm),
    queryFn: async () => await fetchModel<BuyerModel[]>('buyer', farm),
    enabled: !!farm,
  });

  const modelQueries = useMemo(
    () => [farmsQuery, licenseModeQuery, farmInfoQuery, dietsQuery, areasQuery, lotsQuery, gainCurvesQuery, programsQuery, itemsQuery, suppliersQuery, buyersQuery],
    [farmsQuery, licenseModeQuery, farmInfoQuery, dietsQuery, areasQuery, lotsQuery, gainCurvesQuery, programsQuery, itemsQuery, suppliersQuery, buyersQuery]
  );

  useEffect(() => {
    if (farmsQuery.data && farm) {
      const farmExists = farmsQuery.data.some((farmItem) => farmItem.id === farm);
      if (!farmExists) {
        setFarm(undefined);
        localStorage.removeItem(FARM_STORAGE_KEY);
      }
    }
  }, [farm, farmsQuery.data]);

  useEffect(() => {
    if (farmsQuery.data) {
      setUpdate((prev) => !prev);
    }
  }, [farmsQuery.data]);

  useEffect(() => {
    modelQueries.forEach((query) => {
      if (query.error) {
        onError(query.error);
      }
    });
  }, [modelQueries]);

  return (
    <FarmContext.Provider
      value={{
        farm,
        farms: farmsQuery.data,
        update,
        selectFarm,
        farmInfo: farmInfoQuery.data ?? undefined,
        diets: dietsQuery.data ?? undefined,
        areas: areasQuery.data ?? undefined,
        lots: lotsQuery.data ?? undefined,
        gainCurves: gainCurvesQuery.data ?? undefined,
        programs: programsQuery.data ?? undefined,
        items: itemsQuery.data ?? undefined,
        selectedDiet,
        suppliers: suppliersQuery.data ?? undefined,
        buyers: buyersQuery.data ?? undefined,
        licenseMode: licenseModeQuery.data?.mode,
        licenseFeatures: licenseModeQuery.data?.features,
        setSelectedDiet,
      }}
    >
      {children}
    </FarmContext.Provider>
  );
}
